import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import buildClassNames from 'classnames';
import {
    Link as SitecoreLink,
    withSitecoreContext,
} from '@sitecore-jss/sitecore-jss-react';
import {
    ANALYTICS_EVENT,
    ANALYTICS_NULL_VALUES,
} from '@fhs/shared/src/analytics';
import { Link as RouterLink } from 'react-router-dom';
import Icon, { ICON_NAMES } from '../../components-common/Icon';
import './Link.scss';

/**
 * This link component wraps the sitecore and react router dom router link components and determines
 * which to show based on props from sitecore. Generally we want to use a router link component for
 * internal links and use sitecore link component for editor and external links
 */
const Link = ({
    sitecoreContext,
    // @NOTE: updateSitecoreContext destructured here so its not in props spread
    updateSitecoreContext,
    linkFieldValue,
    options,
    children,
    componentName,
    ...props
}) => {
    const linkRef = useRef(null);
    const { href, linktype, text } = linkFieldValue;
    const isAnchorLink = linktype === 'anchor';
    const isMediaLink = linktype === 'media';
    const isExternalLink = linktype === 'external';
    const isEmailLink = linktype === 'mailto';
    const addExternalLinkIcon = options.addExternalLinkIcon && isExternalLink;

    const gtmClickTracking = (event) => {
        event.stopPropagation();
        const LinkText =
            text || linkRef.current?.text || ANALYTICS_NULL_VALUES.NA_VALUE;

        const gtmClickObject = {
            event: ANALYTICS_EVENT.CLICK,
            clickInfo: {
                clickUrl: href || ANALYTICS_NULL_VALUES.NA_VALUE,
                clickElement: 'a',
                clickText: LinkText,
                clickElementPosition: componentName,
                clickClass: `Link ${props.className}`,
            },
        };

        window.dataLayer.push(gtmClickObject);

        if (props.onClick) {
            props.onClick(event);
        }
    };

    if (
        sitecoreContext.pageEditing ||
        isExternalLink ||
        isMediaLink ||
        isEmailLink
    ) {
        return (
            <SitecoreLink
                field={{ ...linkFieldValue, ref: linkRef }}
                className={buildClassNames('Link', props.className)}
                onClick={gtmClickTracking}>
                {children || text}
                {addExternalLinkIcon && (
                    <span className="icon icon--suffix">
                        <Icon name={ICON_NAMES.EXTERNAL_LINK} />
                    </span>
                )}
            </SitecoreLink>
        );
    }

    if (isAnchorLink) {
        return (
            <a
                href={href}
                rel={
                    linkFieldValue?.target === '_blank'
                        ? 'noopener noreferrer'
                        : null
                }
                target={linkFieldValue?.target || null}
                ref={linkRef}
                className={buildClassNames('Link', props.className)}
                onClick={gtmClickTracking}
                dir={props.dir}
                lang={props.lang}>
                {children || text}
            </a>
        );
    }

    return (
        <RouterLink
            to={href}
            className={props.className}
            dir={props.dir}
            lang={props.lang}>
            {children || text}
        </RouterLink>
    );
};

Link.propTypes = {
    linkFieldValue: PropTypes.shape({
        href: PropTypes.string,
        className: PropTypes.string,
        title: PropTypes.string,
        target: PropTypes.string,
    }).isRequired,
    componentName: PropTypes.string,
    options: PropTypes.shape({
        addExternalLinkIcon: PropTypes.bool,
    }),
    dir: PropTypes.string,
    lang: PropTypes.string,
};

Link.defaultProps = {
    componentName: '',
    options: {
        addExternalLinkIcon: true,
    },
    dir: 'ltr',
    lang: 'en',
};

export default withSitecoreContext()(Link);
